import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Menu, X, Plus, User, UserCircle, LogOut, Package, ChevronDown, ChevronRight, Sun, Moon, Globe, Heart, Bell, ArrowLeftRight, Shield, BarChart3, MessageCircle, Grid3X3, Gavel, Calendar, Building2, Search, Clock, Grid, History, MapPin } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, useTheme, useLanguage, useCurrency, useWebSocket, useIsland, useNotifications, API, LOGO_URL_DARK, LOGO_URL_LIGHT } from '../App';
import { getIconComponent, getSubcategoryIcon } from '../utils/categoryIcons';
import { usePersistedCategory, usePersistedState } from '../hooks/usePersistedState';
import { safeClick } from '../utils/safeClick';
import { getSubcategoryTranslation, getCategoryTranslation } from '../i18n-subcategories';
import NavbarDesktop from './NavbarDesktop';

// Scroll to top utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { currentIsland, setCurrentIsland } = useIsland();
  const { unreadCount: wsUnreadMessages, lastMessage, isConnected, setUnreadCount } = useWebSocket();
  const { unreadCount: unreadNotifications, fetchUnreadCount, refresh: refreshNotifications } = useNotifications();
  
  // Island options with flags
  const ISLAND_OPTIONS = [
    { code: 'aruba', label: 'Aruba', flag: '🇦🇼' },
    { code: 'bonaire', label: 'Bonaire', flag: '🇧🇶' },
    { code: 'curacao', label: 'Curaçao', flag: '🇨🇼' },
  ];
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mobileCategories, setMobileCategories] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [guestMenuOpen, setGuestMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // unreadMessages now comes directly from wsUnreadMessages (WebSocket context)
  const [prevUnreadCount, setPrevUnreadCount] = useState(0);
  const [userBidCount, setUserBidCount] = useState(0);
  const [userEventCount, setUserEventCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Power Search Bar state
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Use persisted category hook for cross-tab sync and flash prevention
  const { 
    category: selectedCategory, 
    setCategory: setSelectedCategory, 
    subcategory: selectedSubcategory, 
    setSubcategory: setSelectedSubcategory 
  } = usePersistedCategory();
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = usePersistedState('recentSearches', []);
  const searchInputRef = useRef(null);
  
  // Autocomplete suggestion state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestedCategories, setSuggestedCategories] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Refs for click-outside detection
  const settingsRef = useRef(null);
  const userMenuRef = useRef(null);
  const guestMenuRef = useRef(null);
  const searchBarRef = useRef(null);

  // Check admin status
  useEffect(() => {
    if (user) {
      axios.get(`${API}/users/me/is-admin`).then(res => {
        setIsAdmin(res.data.is_admin || false);
      }).catch(() => {
        setIsAdmin(false);
      });
    }
  }, [user, token]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (guestMenuRef.current && !guestMenuRef.current.contains(event.target)) {
        setGuestMenuOpen(false);
      }
      // Close category/subcategory dropdowns when clicking outside search bar
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
        setShowSubcategoryDropdown(false);
      }
    };

    // Listen for both mouse and touch events for iOS compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Fetch categories for menu and search (with language parameter)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Add cache-busting timestamp to ensure fresh data
        const timestamp = Date.now();
        const response = await axios.get(`${API}/categories?lang=${language}&_t=${timestamp}`);
        setMobileCategories(response.data);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [language]);

  // Sync URL params with search state for SUBSEQUENT navigations (SPA routing)
  // Initial load is handled by useState initializer in usePersistedCategory
  // This effect only runs when location changes AFTER mount
  useEffect(() => {
    if (location.pathname === '/marketplace') {
      const params = new URLSearchParams(location.search);
      setSearchQuery(params.get('search') || '');
      const urlCategory = params.get('category');
      const urlSubcategory = params.get('subcategory');
      
      // Update category/subcategory from URL
      // If URL has category param (including empty), sync it
      // If URL has no category param but has other params (like search), clear category
      const hasSearchParams = params.toString().length > 0;
      if (urlCategory !== null) {
        setSelectedCategory(urlCategory || '');
        setSelectedSubcategory(urlSubcategory || '');
      } else if (hasSearchParams && !urlCategory) {
        // URL has params but no category - user cleared category filter
        setSelectedCategory('');
        setSelectedSubcategory('');
      }
    } else if (location.pathname === '/categories') {
      // Clear category selection when on /categories page
      setSelectedCategory('');
      setSelectedSubcategory('');
      setSearchQuery('');
    } else {
      // Clear search query when leaving marketplace
      setSearchQuery('');
    }
  }, [location.pathname, location.search, setSelectedCategory, setSelectedSubcategory]);

  // Get active category info
  const activeCategoryInfo = categories.find(c => c.id === selectedCategory);

  // Handle search submission
  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
      // Save to recent searches (persisted automatically via hook)
      const updated = [searchQuery.trim(), ...recentSearches.filter(s => s !== searchQuery.trim())].slice(0, 5);
      setRecentSearches(updated);
    }
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
    
    navigate(`/marketplace?${params.toString()}`);
    setSearchFocused(false);
    setShowSuggestions(false);
    searchInputRef.current?.blur();
  };

  // Fetch autocomplete suggestions (debounced)
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setSuggestedCategories([]);
      setShowSuggestions(false);
      return;
    }
    
    setSuggestionsLoading(true);
    try {
      const params = new URLSearchParams({ q: query, limit: '5' });
      if (currentIsland) {
        params.append('island', currentIsland);
      }
      const response = await axios.get(`${API}/listings/suggest?${params}`);
      setSuggestions(response.data.suggestions || []);
      setSuggestedCategories(response.data.categories || []);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(-1);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
      setSuggestedCategories([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Debounced search input handler
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer (300ms debounce)
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 300);
    
    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, currentIsland]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigate to suggestion
  const handleSuggestionClick = (suggestion) => {
    navigate(`/listing/${suggestion.slug || suggestion.id}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  // Handle category suggestion click - navigate with category set
  const handleCategorySuggestionClick = (categoryId) => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
      // Save to recent searches
      const updated = [searchQuery.trim(), ...recentSearches.filter(s => s !== searchQuery.trim())].slice(0, 5);
      setRecentSearches(updated);
    }
    params.set('category', categoryId);
    
    // Update the persisted category state for navbar sync
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    setShowSuggestions(false);
    
    navigate(`/marketplace?${params.toString()}`);
    searchInputRef.current?.blur();
  };

  // Handle keyboard navigation in suggestions
  const handleSearchKeyDown = (e) => {
    const totalItems = suggestions.length + suggestedCategories.length;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev < totalItems - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        prev > 0 ? prev - 1 : totalItems - 1
      );
    } else if (e.key === 'Enter') {
      if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
        // Selected a listing suggestion
        e.preventDefault();
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else if (selectedSuggestionIndex >= suggestions.length && selectedSuggestionIndex < totalItems) {
        // Selected a category suggestion
        e.preventDefault();
        const catIndex = selectedSuggestionIndex - suggestions.length;
        const cat = suggestedCategories[catIndex];
        if (cat) {
          handleCategorySuggestionClick(cat.id);
        }
      } else {
        // No selection, do normal search
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  };

  // Highlight matching text in suggestion
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-primary/30 text-foreground rounded px-0.5">{part}</mark> : part
    );
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
  };

  // Notification sound effect
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Could not play notification sound');
    }
  };

  // Message unread count (wsUnreadMessages) is now used directly from WebSocket context
  // No sync needed - it's reactive and updates automatically when setUnreadCount is called

  // Note: We no longer refetch when leaving /messages because:
  // 1. The WebSocket context (unreadCount) is the single source of truth
  // 2. MessagesPage updates both local state AND WebSocket context when opening conversations
  // 3. This prevents the "flicker" where old count briefly appears before being corrected
  
  // Note: Sound for new messages is handled in WebSocketContext, not here
  // This prevents duplicate sounds and ensures proper active conversation checking

  // Fetch unread notifications count and user data (NOT messages - that's from WebSocket)
  useEffect(() => {
    if (user) {
      // Initial fetches for non-message data
      fetchUserBidCount();
      fetchUserEventCount();
      
      // Only fetch messages if WebSocket is not connected (fallback)
      if (!isConnected) {
        fetchUnreadMessages();
        // Poll for messages every 30 seconds only if WS is not connected
        const interval = setInterval(() => {
          if (!isConnected) {
            fetchUnreadMessages();
          }
        }, 30000);
        return () => clearInterval(interval);
      }
    }
  }, [user, isConnected]);

  // Fallback: fetch messages from API when WebSocket is not available
  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread/count`);
      // Update WebSocket context directly (it's the single source of truth)
      if (setUnreadCount) {
        setUnreadCount(response.data.count || 0);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const fetchUserBidCount = async () => {
    try {
      const response = await axios.get(`${API}/users/me/bids/count`);
      setUserBidCount(response.data.count || 0);
    } catch (error) {
      // Silently fail - user might not have any bids
      setUserBidCount(0);
    }
  };

  const fetchUserEventCount = async () => {
    try {
      const response = await axios.get(`${API}/events/user/my`);
      setUserEventCount(response.data.total || 0);
    } catch (error) {
      setUserEventCount(0);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* New Desktop Navbar - Two Row Layout */}
      <NavbarDesktop />
      
      {/* Old Desktop Navbar - Now hidden, replaced by NavbarDesktop */}
      <nav className="hidden" data-testid="navbar-old">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[60px] gap-4">
          {/* Left: Logo + Events */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link 
              to="/" 
              className="flex items-center group py-2" 
              data-testid="logo-link"
              onClick={(e) => {
                if (location.pathname === '/') {
                  e.preventDefault();
                  scrollToTop();
                }
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_island-marketplace-3/artifacts/apiogjtg_ICONYBS.png" 
                alt="Your Buy Site" 
                className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
            
            {/* Events Link */}
            <Link 
              to="/events" 
              className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                isActive('/events') 
                  ? 'bg-red-600 text-white' 
                  : 'text-foreground hover:bg-red-600 hover:text-white'
              }`}
              data-testid="nav-events"
            >
              <Calendar className="w-4 h-4" />
              Events
            </Link>
          </div>

          {/* Center: Power Search Bar */}
          <div className="flex-1 max-w-2xl mx-4">
            <div ref={searchBarRef} className="flex items-center h-[36px] bg-secondary/50 border border-border/80 dark:border-white/20 rounded-full overflow-visible relative transition-colors duration-300 focus-within:border-primary/60 dark:focus-within:border-white/60 focus-within:bg-background dark:focus-within:bg-secondary/80">
              {/* Category Dropdown */}
              <div className="relative z-30">
                <button
                  {...safeClick(() => {
                    setShowCategoryDropdown(!showCategoryDropdown);
                    setShowSubcategoryDropdown(false);
                  })}
                  className="flex items-center gap-1.5 h-[36px] px-3 min-w-[80px] lg:min-w-[120px] text-sm font-medium text-foreground hover:bg-secondary/80 transition-colors rounded-l-full border-r border-white/10 touch-manipulation"
                >
                  {selectedCategory ? (
                    // Category is selected - show icon and name
                    activeCategoryInfo ? (
                      // API loaded - use full category info
                      (() => {
                        const ActiveCatIcon = getIconComponent(activeCategoryInfo.icon);
                        return (
                          <>
                            <ActiveCatIcon className="w-4 h-4" />
                            <span className="hidden lg:inline max-w-[100px] truncate">{activeCategoryInfo.name}</span>
                          </>
                        );
                      })()
                    ) : (
                      // API not loaded yet - use fallback icon
                      <>
                        <Package className="w-4 h-4" />
                        <span className="hidden lg:inline max-w-[100px] truncate capitalize">{selectedCategory.replace(/-/g, ' ')}</span>
                      </>
                    )
                  ) : (
                    // No category selected - show "All"
                    <>
                      <Grid className="w-4 h-4 text-muted-foreground" />
                      <span className="hidden lg:inline">All</span>
                    </>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showCategoryDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-[70] py-2 max-h-80 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedCategory('');
                          setSelectedSubcategory('');
                          setShowCategoryDropdown(false);
                          // Navigate to categories page when "All Categories" is selected
                          navigate('/categories');
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                          !selectedCategory ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                        }`}
                      >
                        <Grid className="w-4 h-4" />
                        <span>All Categories</span>
                      </button>
                      {categories.map((cat) => {
                        const CatIcon = getIconComponent(cat.icon);
                        return (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setSelectedCategory(cat.id);
                            setSelectedSubcategory('');
                            setShowCategoryDropdown(false);
                            // Navigate immediately to marketplace with selected category
                            const params = new URLSearchParams();
                            params.set('category', cat.id);
                            if (searchQuery.trim()) params.set('search', searchQuery.trim());
                            navigate(`/marketplace?${params.toString()}`);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 transition-colors ${
                            selectedCategory === cat.id ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                          }`}
                        >
                          <CatIcon className="w-4 h-4" />
                          <span>{cat.name}</span>
                        </button>
                      );})}
                    </div>
                )}
              </div>
              
              {/* Subcategory Dropdown - Show when category selected */}
              {/* Renders: actual dropdown when data ready, skeleton placeholder while loading, nothing if no subcategories */}
              {selectedCategory && (
                activeCategoryInfo?.subcategories?.length > 0 ? (
                  // Actual subcategory dropdown - data is loaded
                  <div className="relative z-30">
                    <button
                      {...safeClick(() => {
                        setShowSubcategoryDropdown(!showSubcategoryDropdown);
                        setShowCategoryDropdown(false);
                      })}
                      className="flex items-center gap-1.5 h-[36px] px-3 min-w-[90px] lg:min-w-[110px] text-sm font-medium text-foreground hover:bg-secondary/80 transition-colors border-r border-border/80 dark:border-white/20 touch-manipulation"
                    >
                      <span className="hidden lg:inline max-w-[100px] truncate">
                        {/* 
                          Display logic:
                          - No subcategory (Landing Page) → "Subcategory" placeholder
                          - "all-*" subcategory (Discovery Mode) → "All"
                          - Specific subcategory → Show the subcategory name (translated)
                        */}
                        {!selectedSubcategory 
                          ? <span className="text-muted-foreground">{t('subcategory')}</span>
                          : selectedSubcategory.startsWith('all-') 
                            ? t('all') 
                            : (() => {
                                // Find the full subcategory object to get the translated name
                                const subObj = activeCategoryInfo?.subcategories?.find(s => 
                                  (typeof s === 'object' ? s.id : s) === selectedSubcategory
                                );
                                if (subObj && typeof subObj === 'object' && subObj.name) {
                                  return getSubcategoryTranslation(subObj.name, language);
                                }
                                return getSubcategoryTranslation(selectedSubcategory, language);
                              })()
                        }
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showSubcategoryDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showSubcategoryDropdown && (
                      <div className="absolute top-full left-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-xl z-[70] py-2 max-h-64 overflow-y-auto">
                        <button
                          onClick={() => {
                            setSelectedSubcategory(`all-${selectedCategory}`);
                            setShowSubcategoryDropdown(false);
                            // Navigate to Discovery Grid with all-[category] pattern
                            const params = new URLSearchParams();
                            params.set('category', selectedCategory);
                            params.set('subcategory', `all-${selectedCategory}`);
                            if (searchQuery.trim()) params.set('search', searchQuery.trim());
                            navigate(`/marketplace?${params.toString()}`);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                            selectedSubcategory?.startsWith('all-') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                          }`}
                        >
                          {t('all')} {activeCategoryInfo.name}
                        </button>
                        {activeCategoryInfo.subcategories.map((sub) => {
                          const subId = typeof sub === 'object' ? sub.id : sub;
                          const subName = typeof sub === 'object' ? sub.name : sub;
                          return (
                          <button
                            key={subId}
                            onClick={() => {
                              setSelectedSubcategory(subId);
                              setShowSubcategoryDropdown(false);
                              // Navigate with category and subcategory
                              const params = new URLSearchParams();
                              params.set('category', selectedCategory);
                              params.set('subcategory', subId);
                              if (searchQuery.trim()) params.set('search', searchQuery.trim());
                              navigate(`/marketplace?${params.toString()}`);
                            }}
                            className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                              selectedSubcategory === subId ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                            }`}
                          >
                            <span>{getSubcategoryTranslation(subName, language)}</span>
                          </button>
                        );})}
                      </div>
                  )}
                </div>
                ) : !activeCategoryInfo ? (
                  // Skeleton placeholder while categories API is loading - reserves space to prevent layout shift
                  <div className="flex items-center gap-1.5 h-[36px] px-3 min-w-[90px] lg:min-w-[110px] text-sm border-r border-border/80 dark:border-white/20">
                    <span className="hidden lg:inline text-muted-foreground/50 animate-pulse">Subcategory</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/30" />
                  </div>
                ) : null
              )}
              
              {/* Search Input with Autocomplete */}
              <div className="relative flex-1 z-20 h-full border-l border-l-white/10 bg-white/[0.03] transition-all duration-300 focus-within:bg-white/[0.08] focus-within:border-l-white/40 self-stretch flex items-center">
                {/* Search icon or loading spinner */}
                {suggestionsLoading ? (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4">
                    <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  </div>
                ) : (
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/80 dark:text-white/70" />
                )}
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={
                    selectedCategory 
                      ? t('search_category').replace('{category}', activeCategoryInfo?.name || selectedCategory.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
                      : t('search_marketplace')
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    setSearchFocused(true);
                    setShowCategoryDropdown(false);
                    setShowSubcategoryDropdown(false);
                    if (searchQuery.length >= 2) setShowSuggestions(true);
                  }}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full h-full pl-10 pr-10 text-sm bg-transparent text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-white/50 focus:outline-none"
                  data-testid="navbar-search-input"
                  autoComplete="off"
                />
                
                {/* Clear search button */}
                {searchQuery && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSearchQuery('');
                      setShowSuggestions(false);
                      
                      // If on marketplace page, navigate to clear the search param from URL
                      if (location.pathname === '/marketplace') {
                        const params = new URLSearchParams(location.search);
                        params.delete('search');
                        params.delete('q');
                        navigate(`/marketplace?${params.toString()}`, { replace: true });
                      }
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 transition-colors"
                    data-testid="clear-search-btn"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground dark:text-white/60" />
                  </button>
                )}
                
                {/* Recent Searches Dropdown */}
                {searchFocused && recentSearches.length > 0 && !searchQuery && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-[80] overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <History className="w-3.5 h-3.5" />
                        Recent
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          clearRecentSearches();
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="py-1">
                      {recentSearches.map((term, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            // Set the search query for display
                            setSearchQuery(term);
                            // Navigate directly with the term (don't rely on async state update)
                            const params = new URLSearchParams();
                            params.set('search', term);
                            if (selectedCategory) params.set('category', selectedCategory);
                            if (selectedSubcategory) params.set('subcategory', selectedSubcategory);
                            navigate(`/marketplace?${params.toString()}`);
                            setSearchFocused(false);
                            setShowSuggestions(false);
                            searchInputRef.current?.blur();
                          }}
                          className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/50 flex items-center gap-2 transition-colors"
                        >
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="truncate">{term}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Autocomplete Suggestions Dropdown */}
                {showSuggestions && searchQuery.length >= 2 && (suggestions.length > 0 || suggestedCategories.length > 0) && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-[80] overflow-hidden max-h-[400px] overflow-y-auto"
                    data-testid="search-suggestions-dropdown"
                  >
                    {/* Direct Listing Matches */}
                    {suggestions.length > 0 && (
                      <div className="p-2">
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Listings
                        </div>
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={suggestion.id}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors ${
                              selectedSuggestionIndex === index 
                                ? 'bg-primary/10 text-primary' 
                                : 'hover:bg-secondary/50'
                            }`}
                            data-testid={`suggestion-${index}`}
                          >
                            {/* Thumbnail */}
                            <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                              {suggestion.image ? (
                                <img 
                                  src={suggestion.image} 
                                  alt="" 
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <Package className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                            
                            {/* Details */}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">
                                {highlightMatch(suggestion.title, searchQuery)}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                <span className="capitalize">{suggestion.category?.replace(/-/g, ' ')}</span>
                                {suggestion.price && (
                                  <>
                                    <span>•</span>
                                    <span className="text-primary font-medium">
                                      {suggestion.currency === 'XCG' ? '' : '$'}{suggestion.price?.toLocaleString()} {suggestion.currency === 'XCG' ? 'XCG' : ''}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            
                            {/* Arrow */}
                            <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Category Suggestions */}
                    {suggestedCategories.length > 0 && (
                      <div className={`p-2 ${suggestions.length > 0 ? 'border-t border-border' : ''}`}>
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Search in Category
                        </div>
                        {suggestedCategories.map((cat, index) => {
                          const catIndex = suggestions.length + index;
                          const SugCatIcon = getIconComponent(cat.icon);
                          return (
                            <button
                              key={cat.id}
                              onClick={() => handleCategorySuggestionClick(cat.id)}
                              className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left transition-colors ${
                                selectedSuggestionIndex === catIndex 
                                  ? 'bg-primary/10 text-primary' 
                                  : 'hover:bg-secondary/50'
                              }`}
                              data-testid={`category-suggestion-${index}`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                                <SugCatIcon className="w-4 h-4" />
                              </div>
                              <div className="flex-1">
                                <span className="text-sm text-foreground">
                                  Search "<span className="font-medium">{searchQuery}</span>" in{' '}
                                  <span className="font-medium text-primary">{cat.name}</span>
                                </span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({cat.match_count} {cat.match_count === 1 ? 'result' : 'results'})
                                </span>
                              </div>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Footer hint */}
                    <div className="px-4 py-2 bg-secondary/30 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
                      <span>
                        <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">↑</kbd>
                        <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono ml-1">↓</kbd>
                        <span className="ml-1.5">to navigate</span>
                      </span>
                      <span>
                        <kbd className="px-1.5 py-0.5 bg-secondary rounded text-[10px] font-mono">Enter</kbd>
                        <span className="ml-1.5">to select</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Search Button */}
              <button 
                onClick={handleSearch}
                className="h-[36px] px-5 bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors rounded-r-full"
              >
                Search
              </button>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 flex-shrink-0">
            
            {/* Create Button - Compact version */}
            <Link
              to={user ? "/create-listing" : "/register"}
              className="hidden sm:flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-medium bg-primary text-white hover:bg-primary/90 rounded-full transition-all"
              data-testid="nav-create-listing"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden md:inline">Create</span>
            </Link>
            
            {/* Currency Switcher - Always visible */}
            <button
              onClick={() => setCurrency(currency === 'XCG' ? 'USD' : 'XCG')}
              className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors"
              data-testid="nav-currency-switcher"
              title={`Switch to ${currency === 'XCG' ? 'USD' : 'XCG'}`}
            >
              <span className="text-sm font-bold text-foreground">
                {currency === 'XCG' ? 'XCG' : '$'}
              </span>
            </button>
            
            {user ? (
              <>
                {/* Utility Icons Group - Messages + Notifications */}
                <div className="hidden sm:flex items-center gap-1">
                  {/* Messages */}
                  <Link
                    to="/messages"
                    className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors relative"
                    data-testid="nav-messages"
                    title="Messages"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {wsUnreadMessages > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulseGlow">
                        {wsUnreadMessages > 9 ? '9+' : wsUnreadMessages}
                      </span>
                    )}
                  </Link>
                  
                  {/* Notifications */}
                  <Link
                    to="/notifications"
                    className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-secondary transition-colors relative"
                    data-testid="nav-notifications"
                    title="Notifications"
                    onClick={() => {
                      // Refresh notifications when clicking bell
                      refreshNotifications && refreshNotifications();
                    }}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulseGlow">
                        {unreadNotifications > 9 ? '9+' : unreadNotifications}
                      </span>
                    )}
                  </Link>
                </div>

                {/* User Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    {...safeClick(() => setUserMenuOpen(!userMenuOpen))}
                    className="flex items-center gap-1.5 h-9 pl-1 pr-2 rounded-full hover:bg-secondary transition-all duration-200 cursor-pointer touch-manipulation"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-7 h-7 rounded-full overflow-hidden ring-1 ring-border/50 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-primary to-blue-600 text-white text-[10px] font-semibold">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        user.name?.charAt(0).toUpperCase()
                      )}
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 hidden sm:block ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="fixed sm:absolute inset-x-4 sm:inset-x-auto sm:right-0 top-16 sm:top-auto sm:mt-3 w-auto sm:w-64 bg-card rounded-2xl shadow-2xl border border-border py-3 z-[100]" data-testid="user-dropdown">
                      <div className="px-5 py-3 border-b border-border">
                        <p className="font-semibold text-foreground truncate">{user.business_name || user.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        {user.is_verified_business && (
                          <span className="verified-badge mt-2 text-xs">
                            ✓ {t('verified_business')}
                          </span>
                        )}
                      </div>
                      <div className="py-2">
                        <Link
                          to="/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                          data-testid="dropdown-profile"
                        >
                          <User className="w-5 h-5 text-muted-foreground" />
                          {t('profile')}
                        </Link>
                        <Link
                          to="/my-listings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                            data-testid="dropdown-my-listings"
                          >
                            <Package className="w-5 h-5 text-muted-foreground" />
                            {t('my_listings')}
                          </Link>
                          <Link
                            to="/my-events"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                            data-testid="dropdown-my-events"
                          >
                            <Calendar className="w-5 h-5 text-muted-foreground" />
                            My Events
                            {userEventCount > 0 && (
                              <span className="ml-auto px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">
                                {userEventCount}
                              </span>
                            )}
                          </Link>
                          <Link
                            to="/my-bids"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                            data-testid="dropdown-my-bids"
                          >
                            <Gavel className="w-5 h-5 text-muted-foreground" />
                            My Bids
                            {userBidCount > 0 && (
                              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                {userBidCount}
                              </span>
                            )}
                          </Link>
                          {/* Messages removed from dropdown - now in header icon bar */}
                          <Link
                            to="/favorites"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                            data-testid="dropdown-favorites"
                          >
                            <Heart className="w-5 h-5 text-muted-foreground" />
                            Favorites
                          </Link>
                          <Link
                            to="/trade-proposals"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                            data-testid="dropdown-trade-proposals"
                          >
                            <ArrowLeftRight className="w-5 h-5 text-muted-foreground" />
                            Trade Proposals
                          </Link>
                          {isAdmin && (
                            <Link
                              to="/admin"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-3 px-5 py-3 text-foreground hover:bg-secondary transition-colors rounded-xl mx-2"
                              data-testid="dropdown-admin"
                            >
                              <Shield className="w-5 h-5 text-muted-foreground" />
                              Admin Panel
                            </Link>
                          )}
                        </div>
                        
                        {/* Settings Section */}
                        <div className="border-t border-border pt-3 mt-1 px-3">
                          {/* Theme Toggle */}
                          <div className="flex items-center justify-between px-2 py-2">
                            <span className="text-sm text-foreground">Theme</span>
                            <button
                              onClick={toggleTheme}
                              className="p-2 rounded-lg hover:bg-secondary transition-colors"
                              data-testid="theme-toggle-btn"
                            >
                              {theme === 'dark' ? (
                                <Sun className="w-5 h-5 text-amber-400" />
                              ) : (
                                <Moon className="w-5 h-5 text-slate-600" />
                              )}
                            </button>
                          </div>
                          
                          {/* Language */}
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</span>
                            <div className="flex gap-1.5 mt-2">
                              {[
                                { code: 'en', label: 'EN', flag: '🇺🇸' },
                                { code: 'nl', label: 'NL', flag: '🇳🇱' },
                                { code: 'es', label: 'ES', flag: '🇪🇸' },
                              ].map((lang) => (
                                <button
                                  key={lang.code}
                                  onClick={() => setLanguage(lang.code)}
                                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    language === lang.code 
                                      ? 'bg-primary text-white' 
                                      : 'bg-secondary hover:bg-muted text-foreground'
                                  }`}
                                >
                                  <span>{lang.flag}</span>
                                  <span>{lang.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Currency */}
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</span>
                            <div className="flex gap-1.5 mt-2">
                              {[
                                { value: 'USD', label: '$ USD' },
                                { value: 'XCG', label: 'XCG' },
                              ].map((opt) => (
                                <button
                                  key={opt.value}
                                  onClick={() => setCurrency(opt.value)}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    currency === opt.value
                                      ? 'bg-primary text-white'
                                      : 'bg-secondary hover:bg-muted text-foreground'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Island */}
                          <div className="px-2 py-2">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Island</span>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {ISLAND_OPTIONS.map((island) => (
                                <button
                                  key={island.code}
                                  onClick={() => setCurrentIsland(island.code)}
                                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                    currentIsland === island.code 
                                      ? 'bg-primary text-white' 
                                      : 'bg-secondary hover:bg-muted text-foreground'
                                  }`}
                                >
                                  <span>{island.flag}</span>
                                  <span>{island.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-t border-border pt-2 mt-1">
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 w-full px-5 py-3 text-red-500 hover:bg-red-500/10 transition-colors rounded-xl mx-2"
                            style={{ width: 'calc(100% - 1rem)' }}
                            data-testid="dropdown-logout"
                          >
                            <LogOut className="w-5 h-5" />
                            {t('sign_out')}
                          </button>
                        </div>
                      </div>
                  )}
                </div>
              </>
            ) : (
              /* Guest User - Avatar with Dropdown */
              <div className="relative" ref={guestMenuRef}>
                <button
                  {...safeClick(() => setGuestMenuOpen(!guestMenuOpen))}
                  className="flex items-center gap-2.5 p-1.5 rounded-full hover:bg-secondary transition-all duration-200 cursor-pointer touch-manipulation"
                  data-testid="guest-menu-btn"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                    <UserCircle className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 hidden sm:block ${guestMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {guestMenuOpen && (
                  <div className="fixed sm:absolute inset-x-4 sm:inset-x-auto sm:right-0 top-16 sm:top-auto sm:mt-3 w-auto sm:w-64 bg-card rounded-2xl shadow-2xl border border-border py-3 z-[100]" data-testid="guest-dropdown">
                    {/* Auth Buttons */}
                    <div className="px-4 pb-3 space-y-2">
                      <Link
                        to="/auth"
                        onClick={() => setGuestMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                        data-testid="guest-signin"
                      >
                        {t('sign_in')}
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setGuestMenuOpen(false)}
                        className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium border border-border text-foreground rounded-xl hover:bg-secondary transition-colors"
                        data-testid="guest-signup"
                      >
                        {t('get_started')}
                      </Link>
                    </div>
                    
                    {/* Settings Section */}
                    <div className="border-t border-border pt-3 px-3">
                      {/* Theme Toggle */}
                      <div className="flex items-center justify-between px-2 py-2">
                        <span className="text-sm text-foreground">Theme</span>
                        <button
                          onClick={toggleTheme}
                          className="p-2 rounded-lg hover:bg-secondary transition-colors"
                          data-testid="guest-theme-toggle"
                        >
                          {theme === 'dark' ? (
                            <Sun className="w-5 h-5 text-amber-400" />
                          ) : (
                            <Moon className="w-5 h-5 text-slate-600" />
                          )}
                        </button>
                      </div>
                      
                      {/* Language */}
                      <div className="px-2 py-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Language</span>
                        <div className="flex gap-1.5 mt-2">
                          {[
                            { code: 'en', label: 'EN', flag: '🇺🇸' },
                            { code: 'nl', label: 'NL', flag: '🇳🇱' },
                            { code: 'es', label: 'ES', flag: '🇪🇸' },
                          ].map((lang) => (
                            <button
                              key={lang.code}
                              onClick={() => setLanguage(lang.code)}
                              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                language === lang.code 
                                  ? 'bg-primary text-white' 
                                  : 'bg-secondary hover:bg-muted text-foreground'
                              }`}
                            >
                              <span>{lang.flag}</span>
                              <span>{lang.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Currency */}
                      <div className="px-2 py-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currency</span>
                        <div className="flex gap-1.5 mt-2">
                          {[
                            { value: 'USD', label: '$ USD' },
                            { value: 'XCG', label: 'XCG' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => setCurrency(opt.value)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                currency === opt.value
                                  ? 'bg-primary text-white'
                                  : 'bg-secondary hover:bg-muted text-foreground'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Island */}
                      <div className="px-2 py-2">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Island</span>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {ISLAND_OPTIONS.map((island) => (
                            <button
                              key={island.code}
                              onClick={() => setCurrentIsland(island.code)}
                              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                currentIsland === island.code 
                                  ? 'bg-primary text-white' 
                                  : 'bg-secondary hover:bg-muted text-foreground'
                              }`}
                            >
                              <span>{island.flag}</span>
                              <span>{island.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              {...safeClick(() => setMobileMenuOpen(!mobileMenuOpen))}
              className="md:hidden p-2.5 text-foreground hover:bg-secondary rounded-full transition-colors touch-manipulation"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border max-h-[calc(100vh-64px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="container mx-auto px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-xl font-medium text-sm ${isActive('/') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'}`}
            >
              {t('home')}
            </Link>
            <Link
              to="/marketplace"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-3 rounded-xl font-medium text-sm ${isActive('/marketplace') ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'}`}
            >
              {t('marketplace')}
            </Link>
            
            {/* Categories Section */}
            <div className="pt-1">
              <button
                {...safeClick(() => setMobileCategoriesOpen(!mobileCategoriesOpen))}
                className="w-full px-4 py-3 rounded-xl font-medium text-sm text-foreground hover:bg-secondary flex items-center justify-between touch-manipulation"
              >
                <span className="flex items-center gap-2">
                  <Grid3X3 className="w-4 h-4 text-primary" />
                  Browse Categories
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileCategoriesOpen && (
                <div className="mt-2 space-y-1 px-1 max-h-64 overflow-y-auto">
                  {mobileCategories.map((category) => {
                    const MobileCatIcon = getIconComponent(category.icon);
                    return (
                    <Link
                      key={category.id}
                      to={`/marketplace?category=${category.id}`}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobileCategoriesOpen(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/70 transition-all"
                    >
                      <MobileCatIcon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium text-sm text-foreground">{category.name}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0" />
                    </Link>
                  );})}
                </div>
              )}
            </div>

            {/* Language & Currency Section - Mobile Only */}
            <div className="border-t border-border mt-3 pt-3">
              <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Settings</p>
              <div className="px-3 py-2 bg-secondary/30 rounded-xl space-y-3">
                {/* Theme Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Theme</span>
                  <button
                    onClick={toggleTheme}
                    className="flex items-center gap-2 px-3 py-1.5 rounded bg-muted hover:bg-muted/80 text-xs font-medium"
                    data-testid="mobile-theme-toggle"
                  >
                    {theme === 'dark' ? (
                      <>
                        <Moon className="w-3.5 h-3.5" />
                        Dark
                      </>
                    ) : (
                      <>
                        <Sun className="w-3.5 h-3.5" />
                        Light
                      </>
                    )}
                  </button>
                </div>
                {/* Language */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Language</span>
                  <div className="flex gap-1">
                    {['en', 'nl', 'es'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setLanguage(lang)}
                        className={`px-2.5 py-1 rounded text-xs font-medium ${language === lang ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
                      >
                        {lang === 'en' ? '🇺🇸' : lang === 'nl' ? '🇳🇱' : '🇪🇸'}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Currency */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Currency</span>
                  <div className="flex gap-1">
                    {[{v: 'USD', l: '$ USD'}, {v: 'XCG', l: 'XCG'}].map((c) => (
                      <button
                        key={c.v}
                        onClick={() => setCurrency(c.v)}
                        className={`px-3 py-1.5 rounded text-xs font-medium ${currency === c.v ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
                      >
                        {c.l}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Island Switcher */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Island</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {ISLAND_OPTIONS.map((island) => (
                      <button
                        key={island.code}
                        onClick={() => setCurrentIsland(island.code)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all ${
                          currentIsland === island.code 
                            ? 'bg-primary text-white' 
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                        data-testid={`mobile-island-${island.code}`}
                      >
                        <span>{island.flag}</span>
                        <span className="hidden xs:inline">{island.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {user && (
              <Link
                to="/create-listing"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm text-primary border border-primary/30 hover:border-primary/50 rounded-xl text-center mt-3 font-medium"
              >
                <Plus className="w-4 h-4" />
                {t('create_listing')}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;
